import * as React from 'react';

import { Hexinho } from '@/componentes/hexagono';
import type { EstiloComToken } from '@/design/estilo';
import { corDaFatia, gradeDoFavo, pontosHexagono, repartirCelulas } from '@/design/favo';
import { formatarPercentualPontos } from '@/design/formato';
import { cn } from '@/lib/utils';

/* CascataPreco — para onde vai cada real do ticket.
 *
 * Duas leituras do mesmo dado, lado a lado, e a graça está em serem duas:
 *
 *   O FAVO. Cem células, cada uma um por cento. Uma barra empilhada mostra
 *   PROPORÇÃO; o favo mostra QUANTIDADE, e dá para CONTAR as células que
 *   sobram. Contar é o que faz o número virar juízo.
 *   A CASCATA. A mesma repartição em linhas, com nome, valor, percentual e
 *   quanto ainda resta depois de cada subtração.
 *
 * Apontar (ou fixar) uma linha acende o grupo dela no favo. Sem isso a
 * legenda e o gráfico são duas coisas que o leitor precisa casar sozinho.
 *
 * ── Sobre a cor das fatias ───────────────────────────────────────────────
 * A escala é SEQUENCIAL de uma matiz só, com um acento fora dela para o que
 * fica (`acento: true`). Não é paleta categórica, e isso foi medido: quatro
 * matizes soltas colapsaram a ΔE2000 2,1 sob protanopia, contra o piso de
 * 6,4 do produto. Ver `design/favo.ts`.
 *
 * A cor nunca é a única pista: cada linha traz nome, valor, percentual e a
 * contagem de células, e o favo preenche na ordem da cascata. */

export type FatiaCascata = {
  id: string;
  nome: string;
  /** Uma linha dizendo o que entra nesta fatia. */
  nota?: string | undefined;
  /** Já formatado. Normalmente um `<Valor formato="moeda">`. */
  valor: React.ReactNode;
  /** Participação no total, em PONTOS percentuais (54.5 = 54,5%). */
  pct: number;
  /** O que ainda resta do total depois desta subtração. Já formatado. */
  resta?: React.ReactNode;
  /**
   * A fatia que NÃO é custo: o que sobra no fim. Recebe o acento da escala
   * e o campo mint, porque deixou de ser subtração e virou resultado.
   */
  acento?: boolean | undefined;
};

export type CascataPrecoProps = {
  fatias: readonly FatiaCascata[];
  /** Desenha o favo de cem células ao lado da cascata. */
  favo?: boolean | undefined;
  /** Uma linha sob o favo, explicando o que se vê nele. */
  legendaFavo?: React.ReactNode;
  /** Uma linha sob a cascata, normalmente fechando a soma. */
  rodape?: React.ReactNode;
  className?: string | undefined;
};

/** A cor de cada fatia: posição na escala sequencial, ou o acento. */
function coresDasFatias(fatias: readonly FatiaCascata[]): string[] {
  const quantosCustos = fatias.filter((f) => !f.acento).length;
  let posicao = 0;
  return fatias.map((f) => {
    if (f.acento) return 'var(--cascata-acento)';
    const cor = corDaFatia(posicao, quantosCustos);
    posicao += 1;
    return cor;
  });
}

export function CascataPreco({
  fatias,
  favo = true,
  legendaFavo,
  rodape,
  className,
}: CascataPrecoProps) {
  /* Duas fontes de destaque: apontada (cursor) e fixada (clique). A fixada
     vence, senão passar o mouse por cima desfaria o que a pessoa acabou de
     escolher com o teclado. */
  const [apontada, setApontada] = React.useState<string | null>(null);
  const [fixada, setFixada] = React.useState<string | null>(null);
  const emFoco = fixada ?? apontada;

  const cores = React.useMemo(() => coresDasFatias(fatias), [fatias]);
  const celulas = React.useMemo(() => repartirCelulas(fatias.map((f) => f.pct)), [fatias]);

  return (
    <div
      className={cn(
        'grid gap-5',
        favo ? '@coluna-larga:grid-cols-[minmax(0,208px)_minmax(0,1fr)] @painel-largo:grid-cols-[minmax(0,244px)_minmax(0,1fr)] @painel-largo:gap-6' : undefined,
        className,
      )}
    >
      {favo ? (
        <div>
          <Favo
            fatias={fatias}
            cores={cores}
            celulas={celulas}
            {...(emFoco === null ? {} : { emFoco })}
          />
          {legendaFavo ? <p className="apoio-nota mt-3">{legendaFavo}</p> : null}
        </div>
      ) : null}

      <div>
        <ul className="grid gap-0.5">
          {fatias.map((fatia, i) => {
            const cor = cores[i] ?? 'var(--cascata-1)';
            const quantas = celulas[i] ?? 0;
            const apagada = emFoco !== null && emFoco !== fatia.id;
            return (
              <li key={fatia.id}>
                <button
                  type="button"
                  aria-pressed={fixada === fatia.id}
                  onMouseEnter={() => setApontada(fatia.id)}
                  onMouseLeave={() => setApontada(null)}
                  onFocus={() => setApontada(fatia.id)}
                  onBlur={() => setApontada(null)}
                  onClick={() => setFixada((atual) => (atual === fatia.id ? null : fatia.id))}
                  className={cn(
                    'grid w-full grid-cols-[22px_1fr_auto] items-center gap-x-3 gap-y-2 rounded-lg p-3 text-left transition-colors',
                    'hover:bg-lavado focus-visible:bg-lavado aria-pressed:bg-lavado',
                    fatia.acento ? 'bg-mint hover:bg-mint aria-pressed:bg-mint mt-2' : undefined,
                    apagada ? 'opacity-70' : undefined,
                  )}
                >
                  <Hexinho cor={cor} tamanho={16} />

                  <span className={cn('min-w-0 text-corpo', fatia.acento ? 'font-semibold' : 'font-medium')}>
                    {fatia.nome}
                    {fatia.nota ? (
                      <small className="text-tinta-2 mt-px block text-nota font-normal">{fatia.nota}</small>
                    ) : null}
                  </span>

                  <span className="cifra text-right whitespace-nowrap">
                    <span className={cn('block', fatia.acento ? 'text-sub' : 'text-corpo')}>{fatia.valor}</span>
                    <span className="text-tinta-2 mt-px block text-nota font-medium">
                      {formatarPercentualPontos(fatia.pct)} · {quantas} células
                    </span>
                  </span>

                  <span className="trilho col-span-2 col-start-2">
                    <i
                      style={
                        {
                          '--cor': cor,
                          '--atraso': `${i * 90}ms`,
                          width: `${Math.min(100, Math.max(0, fatia.pct))}%`,
                        } as EstiloComToken
                      }
                    />
                  </span>
                </button>

                {fatia.resta ? (
                  <p className="text-tinta-2 flex items-baseline justify-end gap-2 px-3 text-nota">
                    do total ainda restam <b className="numero text-tinta-2 font-semibold">{fatia.resta}</b>
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>

        {rodape ? <p className="apoio-nota mt-4">{rodape}</p> : null}
      </div>
    </div>
  );
}

/* Favo — cem células, cada uma um por cento.
 *
 * Cada hexágono leva um fio de contorno além do preenchimento. Não é
 * decoração: as duas primeiras posições da escala são claras, e sem o fio uma
 * célula de mel claro sobre a nata do cartão não teria fronteira visível. */
function Favo({
  fatias,
  cores,
  celulas,
  emFoco,
}: {
  fatias: readonly FatiaCascata[];
  cores: readonly string[];
  celulas: readonly number[];
  emFoco?: string | undefined;
}) {
  const R = 12;
  const grade = gradeDoFavo(100, 10, R, 9);

  const descricao = fatias
    .map((f, i) => `${f.nome}: ${celulas[i] ?? 0} de cada cem`)
    .join('. ');

  /* De qual fatia é cada uma das cem células. Precalculado num array em vez
     de contado dentro do map: o preenchimento segue a ordem da cascata (o
     kit começa em cima, o que sobra fica no rodapé) e essa ordem tem de ser
     evidente em vez de emergir de um contador mutável. */
  const donoDaCelula: number[] = [];
  celulas.forEach((quantas, iFatia) => {
    for (let k = 0; k < quantas; k++) donoDaCelula.push(iFatia);
  });

  return (
    <svg
      viewBox={`0 0 ${grade.largura} ${grade.altura}`}
      width="100%"
      className="block h-auto"
      style={{ maxWidth: grade.largura }}
      role="img"
      aria-label={`Cem células, cada uma um por cento do total. ${descricao}.`}
    >
      {grade.celulas.map(({ indice, cx, cy }) => {
        const iFatia = donoDaCelula[indice] ?? fatias.length - 1;
        const fatia = fatias[iFatia];
        const apagada = emFoco !== undefined && fatia !== undefined && emFoco !== fatia.id;
        return (
          <polygon
            key={indice}
            points={pontosHexagono(cx, cy, R - 1.5)}
            fill={cores[iFatia] ?? 'var(--cascata-1)'}
            stroke="var(--fio)"
            strokeWidth=".75"
            /* fill-opacity e NÃO opacity: se um dia entrar animação de
               entrada, ela roda com fill-mode both e continuaria cravando
               opacity:1 depois de terminar, engolindo o apagamento. Duas
               propriedades, dois donos. */
            fillOpacity={apagada ? 0.16 : 1}
            className="favo-celula"
          />
        );
      })}
    </svg>
  );
}
